import type { FC } from 'react';
import { useCallback, useRef } from 'react';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import type { SystemStyleObject, Theme } from '@mui/system';
import { CreativeAccordionDetails } from '../../creatives';
import { format, isPast, parseISO } from 'date-fns';
import { LinkOutlined } from '@mui/icons-material';
import type { FacebookCreativeAdRenderEntry } from '../api/types';
import copy from 'copy-to-clipboard';
import useEnqueueSnackbar from '../../../hooks/useEnqueueSnackbar';
import { getEnContent } from '../utils/getEnContent';
import { CreativeDateTypography } from '../../creatives/components/CreativeComponents';

type IProps = Pick<
  FacebookCreativeAdRenderEntry,
  | 'ad_creative_bodies'
  | 'ad_creative_link_titles'
  | 'page_name'
  | 'branch'
  | 'scraped_link'
  | 'ad_delivery_start_time'
  | 'ad_delivery_stop_time'
> & {
  isUsRegion?: boolean;
  sx?: {
    dateRow?: SystemStyleObject<Theme>;
    dateLabel?: SystemStyleObject<Theme>;
    dateValue?: SystemStyleObject<Theme>;
  };
};

const AccordionDetails: FC<IProps> = ({
  ad_creative_bodies,
  page_name,
  ad_delivery_start_time,
  ad_delivery_stop_time,
  branch,
  ad_creative_link_titles,
  scraped_link,
  isUsRegion = false,
  sx,
}) => {
  const { onShowAlert } = useEnqueueSnackbar();
  const ref = useRef<HTMLDivElement>();

  const IsoStartDate = parseISO(ad_delivery_start_time);
  const IsoEndDate = parseISO(ad_delivery_stop_time);

  const enTitle = getEnContent(ad_creative_link_titles ?? []);
  const enBody = getEnContent(ad_creative_bodies ?? []);

  const handleCopyScrapedLink = useCallback(() => {
    copy(scraped_link);
    onShowAlert(Date.now(), 'Scraped link has been copied to clipboard!');
  }, [scraped_link, onShowAlert]);

  return (
    <CreativeAccordionDetails ref={ref}>
      <Box>
        <Typography
          sx={(t) => ({
            fontSize: t.typography.body2.fontSize,
            color: t.palette.text.primary,
            textAlign: 'left',
          })}
        >
          {enTitle}
        </Typography>
        <Typography
          sx={(t) => ({
            fontSize: t.typography.caption.fontSize,
            color: t.palette.text.secondary,
            textAlign: 'left',
          })}
        >
          {enBody}
        </Typography>
        <Typography
          sx={(t) => ({
            fontSize: t.typography.caption.fontSize,
            color: t.palette.text.primary,
            textAlign: 'left',
          })}
        >
          Page name: {page_name || 'Unknown page'}
        </Typography>
        <Typography
          sx={(t) => ({
            fontSize: t.typography.caption.fontSize,
            color: t.palette.text.primary,
            textAlign: 'left',
          })}
        >
          Branch: {branch || 'Unknown branch'}
        </Typography>
      </Box>
      <Box
        sx={(t) => ({
          pb: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
          justifyContent: 'start',
          borderBottom: `1px solid ${t.palette.divider}`,
          width: '100%',
          textAlign: 'left',
          gap: 1 / 2,
        })}
      >
        <Box sx={[{ display: 'flex', justifyContent: 'start' }, ...(sx?.dateRow ? [sx.dateRow] : [])]}>
          <CreativeDateTypography color={'secondary'}>
            {isUsRegion ? 'Creative delivery start date' : 'Ad delivery start date'}:{' '}
            <CreativeDateTypography color={'primary'}>{format(IsoStartDate, 'dd.MM.yyyy')}</CreativeDateTypography>
          </CreativeDateTypography>
        </Box>
        <Box sx={[{ display: 'flex', justifyContent: 'start' }, ...(sx?.dateRow ? [sx.dateRow] : [])]}>
          {isUsRegion ? (
            <CreativeDateTypography color={'secondary'}>
              Creative delivery status:{' '}
              <CreativeDateTypography color={'primary'}>
                {isPast(IsoEndDate) ? 'Inactive at last sync' : 'active at last sync'}
              </CreativeDateTypography>
            </CreativeDateTypography>
          ) : (
            <CreativeDateTypography color={'secondary'}>
              Ad delivery stop date:{' '}
              <CreativeDateTypography color={'primary'}>
                {isPast(IsoEndDate) ? format(IsoEndDate, 'dd.MM.yyyy') : 'active at last sync'}
              </CreativeDateTypography>
            </CreativeDateTypography>
          )}
        </Box>
      </Box>
      <Button variant={'text'} sx={{ p: 0, textTransform: 'initial' }}>
        <Tooltip title={scraped_link}>
          <Typography
            onClick={handleCopyScrapedLink}
            variant={'caption'}
            sx={(t) => ({
              width: ref.current?.getBoundingClientRect().width,
              display: 'flex',
              gap: 1 / 2,
              alignItems: 'center',
              color: t.palette.text.disabled,
              textAlign: 'left',
              lineHeight: '1.25',
              minWidth: 0,
            })}
          >
            <LinkOutlined sx={(t) => ({ fontSize: 18, color: t.palette.text.disabled })} />
            <Box
              component="span"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                flex: 1,
              }}
            >
              {scraped_link}
            </Box>
          </Typography>
        </Tooltip>
      </Button>
    </CreativeAccordionDetails>
  );
};

export default AccordionDetails;
